import type { AppearanceFont, AppearanceFontSize, AppearanceTheme, ThemeCategory } from "./types";

export type AppearanceThemeOption = {
  id: AppearanceTheme;
  label: string;
  category: ThemeCategory;
  preview: Record<
    "light" | "dark",
    {
      chrome: string;
      canvas: string;
      card: string;
      selected: string;
    }
  >;
};

export type AppearanceFontOption = {
  id: AppearanceFont;
  label: string;
  previewClassName: string;
};

export type AppearanceFontSizeOption = {
  id: AppearanceFontSize;
  label: string;
};

export const APPEARANCE_THEME_OPTIONS: AppearanceThemeOption[] = [
  {
    id: "negative",
    label: "Dailies Gray",
    category: "cinematic",
    preview: {
      light: { chrome: "#f4f5f7", canvas: "#f1f2f4", card: "#ffffff", selected: "#2a2e34" },
      dark: { chrome: "#181c22", canvas: "#252a32", card: "#3c4552", selected: "#b98116" },
    },
  },
  {
    id: "magic-hour",
    label: "Magic Hour",
    category: "single-color",
    preview: {
      light: { chrome: "#ffffff", canvas: "#f4f5f8", card: "#ffffff", selected: "#ffc539" },
      dark: { chrome: "#30343d", canvas: "#1f2228", card: "#3f4650", selected: "#ae7e17" },
    },
  },
  {
    id: "slate",
    label: "Slate",
    category: "single-color",
    preview: {
      light: { chrome: "#e9eff6", canvas: "#f4f7fa", card: "#ffffff", selected: "#5b7899" },
      dark: { chrome: "#32475e", canvas: "#192330", card: "#40566d", selected: "#6d92b8" },
    },
  },
  {
    id: "cobalt",
    label: "Cobalt Wash",
    category: "single-color",
    preview: {
      light: { chrome: "#1c4e91", canvas: "#eaf2fd", card: "#f8fbff", selected: "#cfe0fa" },
      dark: { chrome: "#293d57", canvas: "#182231", card: "#344b68", selected: "#4f73a4" },
    },
  },
  {
    id: "sage",
    label: "Backlot Sage",
    category: "single-color",
    preview: {
      light: { chrome: "#517865", canvas: "#edf6f0", card: "#fbfefb", selected: "#cce7d6" },
      dark: { chrome: "#42594b", canvas: "#1d2921", card: "#4c6353", selected: "#5f806a" },
    },
  },
  {
    id: "tungsten",
    label: "Practical Glow",
    category: "cinematic",
    preview: {
      light: { chrome: "#b85317", canvas: "#fbf3e8", card: "#fffaf4", selected: "#f59e0b" },
      dark: { chrome: "#453d34", canvas: "#26231f", card: "#5a4f43", selected: "#b9853f" },
    },
  },
  {
    id: "daylight",
    label: "Open Sky",
    category: "cinematic",
    preview: {
      light: { chrome: "#176b7b", canvas: "#eaf7f8", card: "#f8fdfd", selected: "#22b8b0" },
      dark: { chrome: "#35464a", canvas: "#20282a", card: "#506669", selected: "#61979c" },
    },
  },
  {
    id: "noir",
    label: "Night Shoot",
    category: "updated-classics",
    preview: {
      light: { chrome: "#171717", canvas: "#f3f3f3", card: "#ffffff", selected: "#404040" },
      dark: { chrome: "#1c1c1c", canvas: "#0c0c0c", card: "#353535", selected: "#707070" },
    },
  },
  {
    id: "velvet",
    label: "Velvet Curtain",
    category: "cinematic",
    preview: {
      light: { chrome: "#4b1d6a", canvas: "#f4edfb", card: "#fbf8ff", selected: "#8b5cf6" },
      dark: { chrome: "#403a4c", canvas: "#24232a", card: "#585168", selected: "#896caf" },
    },
  },
  {
    id: "silver-screen",
    label: "Silver Screen",
    category: "updated-classics",
    preview: {
      light: { chrome: "#475569", canvas: "#e5e8eb", card: "#f7f8f9", selected: "#94a3b8" },
      dark: { chrome: "#3e4c5a", canvas: "#20262d", card: "#526271", selected: "#7890a7" },
    },
  },
  {
    id: "paper",
    label: "Script Page",
    category: "updated-classics",
    preview: {
      light: { chrome: "#6f6559", canvas: "#f1ede6", card: "#fdfbf7", selected: "#ded4c7" },
      dark: { chrome: "#4b4742", canvas: "#242220", card: "#5a554f", selected: "#766d62" },
    },
  },
  {
    id: "charcoal",
    label: "Graphite Sketch",
    category: "updated-classics",
    preview: {
      light: { chrome: "#3e434a", canvas: "#eceff1", card: "#fbfcfd", selected: "#ccd2d7" },
      dark: { chrome: "#41464d", canvas: "#1a1c1f", card: "#555a61", selected: "#6c727a" },
    },
  },
  {
    id: "red-carpet",
    label: "Premiere Velvet",
    category: "cinematic",
    preview: {
      light: { chrome: "#741f2c", canvas: "#faf7f7", card: "#ffffff", selected: "#d84252" },
      dark: { chrome: "#4b3d41", canvas: "#272223", card: "#624f54", selected: "#a96b75" },
    },
  },
  {
    id: "green-room",
    label: "Green Room",
    category: "cinematic",
    preview: {
      light: { chrome: "#126449", canvas: "#ecf6f1", card: "#f8fcfa", selected: "#20a66d" },
      dark: { chrome: "#3a4b44", canvas: "#202723", card: "#536962", selected: "#66a887" },
    },
  },
];

export const APPEARANCE_FONT_OPTIONS: AppearanceFontOption[] = [
  {
    id: "inter-tight",
    label: "Inter Tight",
    previewClassName: "font-[Inter_Tight]",
  },
  {
    id: "lato",
    label: "Lato",
    previewClassName: "font-[Lato]",
  },
  {
    id: "system",
    label: "System",
    previewClassName: "font-sans",
  },
  {
    id: "geometric",
    label: "Geometric",
    previewClassName: "font-[Inter_Tight] tracking-tight",
  },
  {
    id: "courier",
    label: "Courier",
    previewClassName: "font-courier",
  },
  {
    id: "reading",
    label: "Reading",
    previewClassName: "font-serif",
  },
];

export const APPEARANCE_FONT_SIZE_OPTIONS: AppearanceFontSizeOption[] = [
  { id: "compact", label: "Compact" },
  { id: "default", label: "Default" },
  { id: "comfortable", label: "Comfortable" },
  { id: "large", label: "Large" },
];

/** Shared brand button / chip classes — follow active theme via primary tokens. */
export const THEME_BRAND_SURFACE_CLASSES =
  "bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 hover:border-primary/30";

/**
 * Partner-integration accent (Claude), used for controls that hand off to Claude.
 * Anything that stays inside RivetAI keeps the primary/brand tokens instead.
 */
export const THEME_CONNECTOR_SURFACE_CLASSES =
  "bg-connector/15 text-connector border-connector/30 hover:bg-connector/25 hover:border-connector/45";

export function getAppearanceThemeLabel(theme: AppearanceTheme): string {
  return APPEARANCE_THEME_OPTIONS.find((option) => option.id === theme)?.label ?? theme;
}
